import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import Modal from "@mui/material/Modal";
import React, { useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import StorageIcon from "@mui/icons-material/Storage";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import axios from "axios";

import CustomDialog from "./CustomDialog";
import CustomSnackbar from "./CustomSnackbar";
import PageLoader from "./PageLoader";
import {
  buildSyncJobOrg,
  createCronJob,
  cronJobId,
  deleteCronJob,
  jobsForModule,
  listCronJobs,
  updateCronJob,
} from "../utils/api";
import {
  INITIAL_LAST_FETCHED_TIME,
  JOB_TYPES,
} from "../utils/constants";
import { DELUGE_FUNCTIONS } from "../utils/orgVariables";
import { BRAND_COLOR_TINT, SURFACE_OVERLAY } from "../utils/colors";
import {
  FS_BODY,
  FS_CAPTION,
  FS_HEADING,
  FS_MICRO,
  FS_TITLE,
  FW_SEMIBOLD,
} from "../utils/typography";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  // floats over page content — stays opaque, see SURFACE_OVERLAY in utils/colors
  bgcolor: SURFACE_OVERLAY,
  boxShadow: 24,
  borderRadius: "10px",
  p: 4,
};

const ZOHO = window.ZOHO;

const fetchModuleFields = async (moduleNameApiName) => {
  try {
    const moduleFieldsResp = await ZOHO.CRM.META.getFields({
      Entity: moduleNameApiName,
    });

    const fileteredFields = moduleFieldsResp.fields.flatMap((field) => {
      if (
        field.data_type === "subform" ||
        field.data_type === "multiselectlookup"
      ) {
        return [];
      }
      return [
        {
          api_name: field.api_name,
          field_label: field.field_label,
          data_type: field.data_type,
        },
      ];
    });

    return {
      error: null,
      data: fileteredFields,
    };
  } catch (err) {
    return {
      data: null,
      error: err,
    };
  }
};

const Settings = ({ orgId, apiKey, connectionUrl, dataCenterUrl, zapiKey }) => {
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [fetchedModules, setFetchedModules] = useState({});
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentModuleFields, setCurrentModuleFields] = useState([]);
  const [currentDatabaseFields, setCurrentDatabaseFields] = useState([]);
  const [syncDuration, setSyncDuration] = useState(null);
  const [deleteBackupData, setDeleteBackupData] = useState(false);
  const [modules, setModules] = useState(null);
  const [moduleDetails, setModuleDetails] = useState("");
  const [modulesToBeSaved, setModulesToBeSaved] = useState({});
  const [pageMode, setPageMode] = useState("");
  const [showBackdrop, setShowBackDrop] = useState(false);

  const [deleteSettingId, setDeleteSettingId] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const handleOpenModal = (id) => {
    setDeleteSettingId(id);
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setDeleteSettingId();
    setOpenModal(false);
  };

  //snackbar state and functions
  const [openSnack, setOpenSnack] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState(""); // success or error

  const syncDurations = [
    { label: "10 Mins", total_in_minutes: 10 },
    { label: "15 Mins", total_in_minutes: 15 },
    { label: "20 Mins", total_in_minutes: 20 },
    { label: "30 Mins", total_in_minutes: 30 },
    { label: "60 Mins", total_in_minutes: 60 },
    { label: "2 Hours", total_in_minutes: 120 },
    { label: "3 Hours", total_in_minutes: 180 },
    { label: "4 Hours", total_in_minutes: 240 },
    { label: "6 Hours", total_in_minutes: 360 },
    { label: "12 Hours", total_in_minutes: 720 },
    { label: "24 Hours", total_in_minutes: 1440 },
  ];

  useEffect(() => {
    if (!selectedModule) return;
    //if it exists in the fetchedModules then grab info from there
    if (fetchedModules[selectedModule.api_name]) {
      if (pageMode === "addNewMode") {
        setCurrentModuleFields(fetchedModules[selectedModule.api_name]);
      }
      return;
    }

    (async () => {
      try {
        const resposeFetchModuleFields = await fetchModuleFields(
          selectedModule.api_name
        );

        if (resposeFetchModuleFields.data) {
          if (pageMode === "addNewMode") {
            setCurrentModuleFields(resposeFetchModuleFields.data);
          }
          setFetchedModules((prev) => ({
            ...prev,
            [selectedModule.api_name]: resposeFetchModuleFields.data,
          }));

          return;
        }
      } catch (err) {
        setMessage(JSON.stringify(err));
        setSeverity("error");
        handleClickSnack();
      } finally {
      }
    })();
  }, [selectedModule]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const getAllModulesData = await axios.request({
          url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/db/planetscale/clients/settings`,
          method: "GET",
          headers: {
            orgid: orgId, // Org ID
            apikey: apiKey, // API KEy
            connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
          },
        });

        if (getAllModulesData?.data?.data) {
          const moduleNeedToSave = {};
          getAllModulesData?.data?.data.forEach((moduleInfo) => {
            if (moduleInfo.module_name !== "ConnectionSchema") {
              moduleNeedToSave[moduleInfo.module_name] = {
                ...JSON.parse(moduleInfo.setting_schema),
                setting_id: moduleInfo.setting_id,
              };
            }
          });

          setModulesToBeSaved(moduleNeedToSave);
        }
      } catch (err) {
        setMessage(JSON.stringify(err));
        setSeverity("error");
        handleClickSnack();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const modulesResp = await ZOHO.CRM.META.getModules();

      const reducedModules = modulesResp.modules?.reduce((result, module) => {
        if (
          module.visible &&
          module.editable &&
          !module.parent_module?.id &&
          module.global_search_supported &&
          module.generated_type !== "linking"
        ) {
          result.push(module.api_name);
        }
        return result;
      }, []);

      setModules(
        modulesResp.modules?.reduce((result, module) => {
          if (
            module.visible &&
            module.editable &&
            !module.parent_module?.id &&
            module.global_search_supported &&
            module.generated_type !== "linking"
          ) {
            result.push(module);
          }
          return result;
        }, [])
      );
    };
    try {
      setLoading(true);
      fetchData();
    } catch (err) {
      setMessage(JSON.stringify(err));
      setSeverity("error");
      handleClickSnack();
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSyncDuration("");
    setSelectedModule(null);
    setCurrentModuleFields([]);
    setCurrentDatabaseFields([]);
  };

  const handleSave = async () => {
    setShowBackDrop(true);
    if (pageMode === "addNewMode") {
      //save settings to our db
      const saveModuleToDb = await axios.request({
        url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/db/planetscale/clients/settings`,
        method: "POST",
        data: {
          moduleApiName: selectedModule.api_name,
          settingSchema: {
            moduleInfo: {
              api_name: selectedModule.api_name,
              module_name: selectedModule.plural_label,
            },
            moduleDetails: moduleDetails,
            database_fields: currentDatabaseFields,
            syncDuration: syncDuration,
            deleteBackupData: deleteBackupData,
          },
        },
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
        },
      });

      //sync table to postgresql db
      const addTable = await axios.request({
        url: `${process.env.REACT_APP_DBURL}/setting/${selectedModule.api_name}`,
        method: "POST",
        data: {},
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
          connectionUrl,
        },
      });

      if (addTable.data.error) {
        setMessage(addTable.data.error);
        setSeverity("error");
        handleClickSnack();
        setShowBackDrop(false);
        const setting_id = saveModuleToDb.data.data.setting_id;
        // Delete Module data from db (our database)
        const deleteData = await axios.request({
          url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/db/planetscale/clients/settings/${setting_id}`,
          method: "DELETE",
          headers: {
            orgid: orgId, // Org ID
            apikey: apiKey, // API KEy
            connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
          },
        });

        return;
      }

      // v3 dedicated store (§11): drop any existing jobs for this module,
      // then create the recurring sync job — __syncmoduledata on crm/v2,
      // cadence from the picker, fresh cursor from the epoch.
      const existingJobs = jobsForModule(
        await listCronJobs({ orgId, apiKey }),
        selectedModule.api_name
      );
      for (const job of existingJobs) {
        await deleteCronJob({ orgId, apiKey, jobId: cronJobId(job) });
      }

      await createCronJob({
        orgId,
        apiKey,
        job: buildSyncJobOrg({
          orgId,
          apiKey,
          dataCenterUrl,
          zapiKey,
          functionName: DELUGE_FUNCTIONS.SYNC,
          moduleName: selectedModule.api_name,
          type: JOB_TYPES.SYNC,
          connectionUrl,
          executeIn: syncDuration.total_in_minutes,
          extraBody: { lastFetchedTime: INITIAL_LAST_FETCHED_TIME },
        }),
      });

      setModulesToBeSaved((prev) => ({
        ...prev,
        [selectedModule.api_name]: {
          moduleInfo: {
            api_name: selectedModule.api_name,
            module_name: selectedModule.plural_label,
          },
          moduleDetails: moduleDetails,
          setting_id: saveModuleToDb.data.data.setting_id,
          database_fields: currentDatabaseFields,
          syncDuration: syncDuration,
          deleteBackupData: deleteBackupData,
        },
      }));
    } else {
      //get setting id
      //filter data based on settingType=ConnectionSchema
      const response = await axios.request({
        url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/db/planetscale/clients/settings?moduleApiName=${selectedModule.api_name}`,
        method: "GET",
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
        },
      });
      const settingId = response.data.data[0].setting_id;

      if (!settingId) {
        setMessage("Setting id not found");
        setSeverity("error");
        handleClickSnack();
        setShowBackDrop(false);
        return;
      }

      // edit settings to db
      const updateModuleData = await axios.request({
        url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/db/planetscale/clients/settings`,
        method: "POST",
        data: {
          moduleApiName: selectedModule.api_name,
          settingId,
          settingSchema: {
            moduleInfo: {
              api_name: selectedModule.api_name,
              module_name: selectedModule.module_name,
            },
            moduleDetails: moduleDetails,
            database_fields: currentDatabaseFields,
            syncDuration: syncDuration,
            deleteBackupData: deleteBackupData,
          },
        },
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
        },
      });

      //sync table to postgresql db
      const editTable = await axios.request({
        url: `${process.env.REACT_APP_DBURL}/setting/${selectedModule.api_name}`,
        method: "POST",
        data: {},
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
          connectionUrl,
        },
      });

      if (editTable.data.error) {
        setMessage(editTable.data.error);
        setSeverity("error");
        handleClickSnack();
        setShowBackDrop(false);
        return;
      }

      // v3: update the cadence IN PLACE so the job keeps its incremental
      // cursor (§11 — "updates preserve the cursor"). Only if the job has
      // gone missing do we create a fresh one from the epoch.
      const existingSyncJob = jobsForModule(
        await listCronJobs({ orgId, apiKey }),
        selectedModule.api_name
      ).find((job) => job?.body?.type === JOB_TYPES.SYNC);

      if (existingSyncJob) {
        await updateCronJob({
          orgId,
          apiKey,
          jobId: cronJobId(existingSyncJob),
          patch: { execute_in: syncDuration.total_in_minutes },
        });
      } else {
        await createCronJob({
          orgId,
          apiKey,
          job: buildSyncJobOrg({
            orgId,
            apiKey,
            dataCenterUrl,
            zapiKey,
            functionName: DELUGE_FUNCTIONS.SYNC,
            moduleName: selectedModule.api_name,
            type: JOB_TYPES.SYNC,
            connectionUrl,
            executeIn: syncDuration.total_in_minutes,
            extraBody: { lastFetchedTime: INITIAL_LAST_FETCHED_TIME },
          }),
        });
      }

      setModulesToBeSaved((prev) => ({
        ...prev,
        [selectedModule.api_name]: {
          moduleInfo: {
            api_name: selectedModule.api_name,
            module_name: selectedModule.module_name,
          },
          moduleDetails: moduleDetails,
          database_fields: currentDatabaseFields,
          syncDuration: syncDuration,
          deleteBackupData: deleteBackupData,
          setting_id: settingId,
        },
      }));
    }
    setOpen(false);
    setSelectedModule(null);
    setCurrentModuleFields([]);
    setCurrentDatabaseFields([]);
    setSyncDuration(null);
    setDeleteBackupData(false);
    setPageMode("");

    setShowBackDrop(false);
  };
  const handleClickSnack = () => {
    setOpenSnack(true);
  };
  //snackbar
  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };
  const handleDeleteSetting = async (module) => {
    try {
      setDeleteLoading(true);

      // Delete Module data from db (our database)
      const deleteData = await axios.request({
        url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/db/planetscale/clients/settings/${module.setting_id}`,
        method: "DELETE",
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
        },
      });

      if (deleteData.data.error) {
        setMessage(deleteData.data.error);
        setSeverity("error");
        handleClickSnack();
        setShowBackDrop(false);
        return;
      }

      //delete table from postgresql
      const deleteTable = await axios.request({
        url: `${process.env.REACT_APP_DBURL}/setting/${module?.moduleInfo?.api_name}`,
        method: "DELETE",
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
          connectionUrl,
        },
      });

      // v3: module delete removes EVERY job for the module — sync, backfill
      // and restore alike (§11).
      const moduleJobs = jobsForModule(
        await listCronJobs({ orgId, apiKey }),
        module.moduleInfo.api_name
      );
      for (const job of moduleJobs) {
        await deleteCronJob({ orgId, apiKey, jobId: cronJobId(job) });
      }

      const modulesNeedToBeSaved = {};

      Object.values(modulesToBeSaved).forEach((moduleInfo) => {
        if (moduleInfo.setting_id !== module.setting_id) {
          modulesNeedToBeSaved[moduleInfo.moduleInfo.module_name] = moduleInfo;
        }
      });

      setModulesToBeSaved(modulesNeedToBeSaved);

      handleCloseModal();
    } catch (err) {
      setMessage("Connection Url is wrong!");
      setSeverity("error");
      handleClickSnack();
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleModuleChange = (event, value) => {
    setSelectedModule(value);
    setCurrentDatabaseFields([]);
  };

  const handleAddNew = () => {
    setPageMode("addNewMode");
    setSelectedModule(null);
    setSyncDuration({
      label: "60 Mins",
      total_in_minutes: 60,
    });
    setDeleteBackupData(true);
    setCurrentDatabaseFields(null);
    setCurrentModuleFields(null);
    handleClickOpen();
  };

  const handleEditModule = async (module) => {
    setShowBackDrop(true);
    const selectedFields = {};
    const moduleFieldsAgainstApiKey = {};

    const responseFetchModuleFields = await fetchModuleFields(
      module?.moduleInfo.api_name
    );
    module?.database_fields.forEach(
      (field) => (selectedFields[field.api_name + "__" + field.data_type] = field)
    );

    // if something went wrong
    if (responseFetchModuleFields.error) {
      //show error
      return;
    }

    const filteredModuleFields = [];

    responseFetchModuleFields.data.flatMap((field) => {
      //push all those fields to filteredModuleFields that are not selected
      if (!selectedFields[field.api_name + "__" + field.data_type]) {
        filteredModuleFields.push({
          api_name: field.api_name,
          field_label: field.field_label,
          data_type: field.data_type,
          field_exist_in_zoho: true,
        });
      }

      //add field to moduleFieldsAgainstApiKey
      moduleFieldsAgainstApiKey[field.api_name + "__" + field.data_type] = {
        api_name: field.api_name,
        field_label: field.field_label,
        data_type: field.data_type,
      };
    });

    const filteredCurrentDatabaseFields = module.database_fields.map(
      (field, index) => {
        // if field exists in the moduleFieldsAgainstApiKey that means this field exist in that module i mean exist in the zoho
        // else we will add a extra parameter to track the field
        if (moduleFieldsAgainstApiKey[field.api_name + "__" + field.data_type]) {
          return { ...field, field_exist_in_zoho: true };
        } else {
          return { ...field, field_exist_in_zoho: false };
        }
      }
    );

    setPageMode("editMode");

    setTimeout(() => {
      setFetchedModules((prev) => ({
        ...prev,
        [selectedModule]: responseFetchModuleFields.data,
      }));
      setSelectedModule(module?.moduleInfo);
      setSyncDuration(module?.syncDuration);
      setDeleteBackupData(module?.deleteBackupData);

      setCurrentDatabaseFields(filteredCurrentDatabaseFields);
      setCurrentModuleFields(filteredModuleFields);
      handleClickOpen();
      setShowBackDrop(false);
    }, 100);
  };

  if (loading) return <PageLoader message="Loading module settings…" />;

  const savedModules = Object.values(modulesToBeSaved);

  return (
    <Box>
      <Backdrop
        sx={{ color: "primary.main", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CustomSnackbar
        open={openSnack}
        handleClose={handleCloseSnack}
        message={message}
        severity={severity}
      />

      {/* Header */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: FS_HEADING, fontWeight: FW_SEMIBOLD }}>
            Modules
          </Typography>
          <Typography sx={{ fontSize: FS_BODY, color: "text.secondary" }}>
            Choose which CRM modules sync to your PostgreSQL database.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
          Add New Module
        </Button>
      </Box>

      {/* Cards grid */}
      {savedModules.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: BRAND_COLOR_TINT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <StorageIcon color="primary" sx={{ fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontWeight: FW_SEMIBOLD, fontSize: FS_TITLE }}>
            No modules synced yet
          </Typography>
          <Typography sx={{ fontSize: FS_BODY, color: "text.secondary" }}>
            Add a module to start syncing its records to PostgreSQL.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mt: 1.5 }}
            onClick={handleAddNew}
          >
            Add New Module
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 2,
          }}
        >
          {savedModules.map((module) => (
            <Card
              key={module?.moduleInfo.module_name}
              sx={{
                p: 2,
                transition: "box-shadow 0.2s ease-in-out",
                "&:hover": {
                  boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: BRAND_COLOR_TINT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <StorageIcon color="primary" sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: FW_SEMIBOLD,
                      fontSize: FS_TITLE,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {module?.moduleInfo?.module_name}
                  </Typography>
                  {module.moduleDetails && (
                    <Typography sx={{ fontSize: FS_CAPTION, color: "text.secondary" }}>
                      {module.moduleDetails}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={`${module?.database_fields?.length || 0} fields`}
                  sx={{
                    bgcolor: BRAND_COLOR_TINT,
                    fontWeight: FW_SEMIBOLD,
                    fontSize: FS_MICRO,
                  }}
                />
                {module?.syncDuration?.label && (
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                    label={`Every ${module.syncDuration.label}`}
                    sx={{
                    bgcolor: BRAND_COLOR_TINT,
                    fontWeight: FW_SEMIBOLD,
                    fontSize: FS_MICRO,
                  }}
                  />
                )}
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() => handleEditModule(module)}
                >
                  Edit
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  fullWidth
                  onClick={async () => handleOpenModal(module)}
                >
                  Delete
                </Button>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box
            sx={{
              bgcolor: SURFACE_OVERLAY,
              width: 500,
            }}
          >
            <Typography
              sx={{ mt: 4, fontWeight: FW_SEMIBOLD, fontSize: FS_TITLE }}
            >
              The related workflow won't work anymore if you delete this
              settings. Do you wish to proceed?
            </Typography>
            <Box
              sx={{
                mt: 3,
                ml: "1rem",
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
              }}
            >
              <Button
                sx={{ width: 120 }}
                variant="outlined"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>

              <Button
                disabled={deleteLoading}
                sx={{ width: 120 }}
                variant="contained"
                color="error"
                onClick={() => handleDeleteSetting(deleteSettingId)}
              >
                {deleteLoading ? (
                  <CircularProgress sx={{ color: "white", ml: 1 }} size={16} />
                ) : (
                  "Proceed"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      {/* dialog */}

      {pageMode === "editMode" ? (
        <CustomDialog
          selectedModule={selectedModule}
          mode={pageMode}
          open={open}
          handleClose={handleClose}
          modules={modules.filter(
            (module) => !modulesToBeSaved[module.api_name]
          )}
          handleModuleChange={handleModuleChange}
          currentModuleFields={currentModuleFields}
          setCurrentModuleFields={setCurrentModuleFields}
          currentDatabaseFields={currentDatabaseFields}
          setCurrentDatabaseFields={setCurrentDatabaseFields}
          syncDuration={syncDuration}
          setSyncDuration={setSyncDuration}
          deleteBackupData={deleteBackupData}
          setDeleteBackupData={setDeleteBackupData}
          syncDurations={syncDurations}
          handleSave={handleSave}
          syncAllFields={() => {
            setCurrentDatabaseFields(fetchedModules[selectedModule.api_name]);
            setTimeout(() => {
              setCurrentModuleFields([]);
            }, 100);
          }}
          unSelectAllFields={() => {
            setCurrentModuleFields(fetchedModules[selectedModule.api_name]);
            setCurrentDatabaseFields([]);
          }}
          handleDeleteField={(value) => {
            const filteredCurrentDatabaseFields = currentDatabaseFields.filter(
              (field) => field.api_name !== value.api_name
            );

            setCurrentDatabaseFields([...filteredCurrentDatabaseFields]);
          }}
        />
      ) : (
        <CustomDialog
          open={open}
          mode={pageMode}
          selectedModule={selectedModule}
          handleClose={handleClose}
          modules={modules?.filter(
            (module) => !modulesToBeSaved[module.api_name]
          )}
          handleModuleChange={handleModuleChange}
          currentModuleFields={currentModuleFields}
          syncAllFields={() => {
            setCurrentDatabaseFields([
              ...fetchedModules[selectedModule.api_name],
            ]);
            setTimeout(() => {
              setCurrentModuleFields([]);
            }, 100);
          }}
          unSelectAllFields={() => {
            setCurrentModuleFields([
              ...fetchedModules[selectedModule.api_name],
            ]);

            setCurrentDatabaseFields([]);
          }}
          syncDurations={syncDurations}
          setCurrentModuleFields={setCurrentModuleFields}
          currentDatabaseFields={currentDatabaseFields}
          setCurrentDatabaseFields={setCurrentDatabaseFields}
          syncDuration={syncDuration}
          setSyncDuration={setSyncDuration}
          deleteBackupData={deleteBackupData}
          setDeleteBackupData={setDeleteBackupData}
          handleSave={handleSave}
        />
      )}
    </Box>
  );
};

export default Settings;
